import { useQuery } from '@tanstack/react-query'
import { getProcurementSuggestedImageProxy, getProcurementSuggestedImages } from 'api'
import axios from 'axios'
import { JustSpinner, Spinner } from 'components/Loading/Loading'
import { Modal, ModalBody, ModalContent, ModalFooter, ModalHeader } from 'components/ModalV2/Modal'
import { EmptyList } from 'components/Table/EmptyList'
import { ILineItem } from 'pages/Procurement/CreateProcurement'
import React, { useState } from 'react'
import { toast } from 'react-toastify'
import styled from 'styled-components'
import { Button, Flex } from 'styling/GlobalStyles'
import { tokens } from 'styling/tokens'
import { PurchaseRequestItemType } from 'types/procurement'

export const SuggestedImagesModal = ({
  onClose,
  item,
  onSave,
}: {
  onClose: () => void
  item: ILineItem
  onSave: (images: File[]) => void
}) => {
  const { data: images, isLoading: isLoadingImages } = useQuery({
    queryKey: ['suggested-images', item.id, item.name],
    queryFn: () =>
      getProcurementSuggestedImages({
        inventoryItemId:
          item.type === PurchaseRequestItemType.Item ? item.inventoryItemId ?? null : null,
        name: item.name,
      }),
  })
  const [isLoading, setIsLoading] = useState(false)
  const [selectedImages, setSelectedImages] = useState<{ url: string; isHostedOnS3: boolean }[]>([])
  const onImageClick = (image: { url: string; isHostedOnS3: boolean }) => {
    if (selectedImages.includes(image)) {
      setSelectedImages(selectedImages.filter((img) => img.url !== image.url))
    } else {
      setSelectedImages([...selectedImages, image])
    }
  }

  const onAddImages = async () => {
    if (selectedImages.length === 0) {
      toast.error('Please select at least one image')
      return
    }
    setIsLoading(true)

    try {
      const files = await Promise.all(
        selectedImages.map(async (image, index) => {
          if (!image.isHostedOnS3) {
            const response = await getProcurementSuggestedImageProxy(image.url)
            return new File([response], `image-${index}.jpg`, {
              type: response.type || 'image/jpeg',
            })
          }
          const response = await axios.get(image.url, {
            responseType: 'blob',
          })

          return new File([response.data], `image-${index}.jpg`, {
            type: response.data.type || 'image/jpeg',
          })
        }),
      )

      onSave(files)
      onClose()
    } catch (error) {
      toast.error('Failed to add images')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <>
      <Modal onClose={onClose}>
        <ModalContent>
          <ModalHeader>Suggested Images</ModalHeader>
          <ModalBody>
            {isLoadingImages ? (
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  height: '200px',
                  width: '100%',
                }}
              >
                <JustSpinner />
              </div>
            ) : images?.length > 0 ? (
              <Grid>
                {images?.map((image) => (
                  <Image
                    key={image.url}
                    src={image.url}
                    alt={image.url}
                    onClick={() => onImageClick(image)}
                    selected={selectedImages.some((img) => img.url === image.url)}
                  />
                ))}
              </Grid>
            ) : (
              <EmptyList
                title='No suggested images found'
                description='There is no suggested images for this item'
              />
            )}
          </ModalBody>
          <ModalFooter>
            {' '}
            <Flex gap='1rem' justend width='100%'>
              <Button onClick={() => onClose()}>Close</Button>
              <Button onClick={() => onAddImages()} orange disabled={selectedImages.length === 0}>
                Add Images
              </Button>
            </Flex>
          </ModalFooter>
        </ModalContent>
      </Modal>
      <Spinner loading={isLoading} />
    </>
  )
}

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
`

const Image = styled.img<{ selected: boolean }>`
  width: 12.5rem;
  height: 12.5rem;
  cursor: pointer;

  border-radius: ${tokens.radius.chip};

  ${(props) => props.selected && `border: 0.125rem solid ${tokens.accent.base}`};
`
